import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Print as PrintIcon } from '@mui/icons-material';
import { bqApi } from '../../services/api/bqApi';

type RegistrationSummaryRow = {
  참가지역?: string;
  등록번호?: string;
  교회명?: string;
  담당자?: string;
  연락처?: string;
  Sparks참관핀?: number;
  TT참관핀?: number;
  추가식사자?: number;
  Sparks핀_행글라이더?: number;
  Sparks핀_윙러너?: number;
  Sparks핀_스카이스토머?: number;
  TT핀_어드밴처1?: number;
  TT핀_어드밴처2?: number;
  TT핀_챌린지1?: number;
  TT핀_챌린지2?: number;
  Sparks선수수?: number;
  Sparks코치수?: number;
  TT선수수?: number;
  TT코치수?: number;
  참관자수?: number;
  총식권?: number;
  총참가비?: number;
};

const formatNumber = (n: number | undefined | null) => new Intl.NumberFormat().format(Number(n || 0));

const SummaryPage: React.FC<{ row: RegistrationSummaryRow }> = ({ row }) => {
  const totalTickets =
    row.총식권 ??
    (Number(row.Sparks선수수 || 0) +
      Number(row.Sparks코치수 || 0) +
      Number(row.TT선수수 || 0) +
      Number(row.TT코치수 || 0) +
      Number(row.참관자수 || 0));

  return (
    <Box
      className="print-page"
      sx={{
        // Screen
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,

        // Print: fit into printable area (A4 landscape with 10mm margins => 277mm x 190mm)
        '@media print': {
          width: '277mm',
          height: '190mm',
          minHeight: 'unset',
          py: 0,
          alignItems: 'stretch',
          justifyContent: 'stretch',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        className="print-card"
        sx={{
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 3,
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
          '@media print': {
            width: '277mm',
            height: '190mm',
            borderRadius: 0,
            boxSizing: 'border-box',
            // keep some inner padding but still fit inside printable area
            p: '10mm',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
            접수 내역
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {row.교회명 || '교회명 없음'} · {row.등록번호 || ''}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />

        <Grid container spacing={3} className="summary-columns">
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                참가비용 안내
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography color="white">Sparks/T&amp;T 선수: 25,000원</Typography>
                <Typography color="white">Sparks/T&amp;T 코치: 20,000원</Typography>
                <Typography color="white">참관자: 12,000원</Typography>
                <Typography color="white">추가 식사자: 8,000원</Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                Sparks 핀
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                <Typography color="white" variant="body2">
                  행글라이더: {formatNumber(row.Sparks핀_행글라이더)}개
                </Typography>
                <Typography color="white" variant="body2">
                  윙러너: {formatNumber(row.Sparks핀_윙러너)}개
                </Typography>
                <Typography color="white" variant="body2">
                  스카이스토머: {formatNumber(row.Sparks핀_스카이스토머)}개
                </Typography>
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                T&amp;T 핀
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography color="white" variant="body2">
                  어드밴처1: {formatNumber(row.TT핀_어드밴처1)}개
                </Typography>
                <Typography color="white" variant="body2">
                  어드밴처2: {formatNumber(row.TT핀_어드밴처2)}개
                </Typography>
                <Typography color="white" variant="body2">
                  챌린지1: {formatNumber(row.TT핀_챌린지1)}개
                </Typography>
                <Typography color="white" variant="body2">
                  챌린지2: {formatNumber(row.TT핀_챌린지2)}개
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                최종 수령 품목 및 수량
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography color="white" variant="body2">
                  Sparks 참관핀: {formatNumber(row.Sparks참관핀)}개
                </Typography>
                <Typography color="white" variant="body2">
                  T&amp;T 참관핀: {formatNumber(row.TT참관핀)}개
                </Typography>
                <Typography color="white" variant="body2">
                  Sparks 코치 표식: {formatNumber(row.Sparks코치수)}개
                </Typography>
                <Typography color="white" variant="body2">
                  T&amp;T 코치 표식: {formatNumber(row.TT코치수)}개
                </Typography>
                <Typography color="white" variant="body2">
                  총 식권: {formatNumber(totalTickets)}장
                </Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                최종 참가 인원
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography color="white" variant="body2">
                  Sparks 선수: {formatNumber(row.Sparks선수수)}명
                </Typography>
                <Typography color="white" variant="body2">
                  Sparks 코치: {formatNumber(row.Sparks코치수)}명
                </Typography>
                <Typography color="white" variant="body2">
                  T&amp;T 선수: {formatNumber(row.TT선수수)}명
                </Typography>
                <Typography color="white" variant="body2">
                  T&amp;T 코치: {formatNumber(row.TT코치수)}명
                </Typography>
                <Typography color="white" variant="body2">
                  참관자 + 추가 식사자: {formatNumber(row.참관자수)}명
                </Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.2)', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                총 참가비
              </Typography>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 800 }}>
                {formatNumber(row.총참가비)}원
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const BQRegistrationSummaryPrintPage: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RegistrationSummaryRow[]>([]);
  const didAutoPrint = useRef(false);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => String(a.등록번호 || '').localeCompare(String(b.등록번호 || '')));
  }, [rows]);

  useEffect(() => {
    const run = async () => {
      if (!eventId) return;
      try {
        setLoading(true);
        setError(null);
        const data = await bqApi.getRegistrationSummary(eventId);
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err?.message || '접수내역을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [eventId]);

  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (sortedRows.length === 0) return;
    if (didAutoPrint.current) return;
    didAutoPrint.current = true;

    // 렌더링/레이아웃 후 인쇄 다이얼로그 오픈
    const t = window.setTimeout(() => {
      window.print();
    }, 350);
    return () => window.clearTimeout(t);
  }, [loading, error, sortedRows.length]);

  const handlePrint = () => window.print();

  return (
    <Box
      sx={{
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      {/* Print styles */}
      <style>{`
        @media print {
          /* Match Chrome "여백: 기본"에 가까운 값으로 고정해서 잘림 방지 */
          @page { size: A4 landscape; margin: 10mm; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; break-after: page; margin: 0 !important; }
          body { margin: 0; }

          /* Force 2-column layout in print (prevent MUI breakpoint collapse) */
          .summary-columns { display: flex !important; flex-wrap: nowrap !important; }
          .summary-columns > .MuiGrid-item {
            flex-basis: 50% !important;
            max-width: 50% !important;
          }
        }
      `}</style>

      <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 5, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">BQ 접수내역 PDF 출력</Typography>
            <Typography variant="body2" color="text.secondary">
              인쇄 창에서 “PDF로 저장”을 선택하세요. (배경 그래픽/색상 출력 옵션을 켜면 화면과 동일하게 나옵니다)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
              뒤로
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
              인쇄 / PDF 저장
            </Button>
          </Box>
        </Container>
      </Box>

      {loading && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>접수내역을 불러오는 중...</Typography>
          </Box>
        </Container>
      )}

      {error && (
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      )}

      {!loading && !error && sortedRows.length === 0 && (
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Alert severity="info">출력할 접수내역이 없습니다.</Alert>
        </Container>
      )}

      {sortedRows.map((row, idx) => (
        <SummaryPage key={`${row.등록번호 || 'unknown'}_${idx}`} row={row} />
      ))}
    </Box>
  );
};

export default BQRegistrationSummaryPrintPage;


